import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigurationService } from '../../../services/configuration/configuration.service';
import { environment } from 'src/environments/environment';
import agentApiConfig from '../../../app/agents/config/api_endpoints.json';
import { TokenService } from '../token/token.service';

/**
 * Shared Agent API Service
 * 
 * Central service for managing agent-related API configurations and requests.
 * Can be used by both agents and potentially chatbot in the future.
 * 
 * Features:
 * - Loads API configuration from api_endpoints.json (per-agent configs)
 * - Uses dedicated agent API base URLs (separate from chatbot APIs)
 * - Provides dynamic URL resolution via getApiUrlFromConfig()
 * - Agent-specific routing: only agents with config in api_endpoints.json use those URLs
 * - Handles HTTP requests with authentication
 * - Supports SSE (Server-Sent Events) streaming
 * - Future-proof for multiple modules
 */
@Injectable({
  providedIn: 'root'
})
export class AgentApiService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigurationService);
  private readonly tokenService = inject(TokenService);

  /**
   * Get the agent-specific configuration block from api_endpoints.json
   * @param agentId - Agent ID (e.g., 'via_agent')
   * @returns The agent-specific config object, or null if not found
   */
  private getAgentBlock(agentId?: string): any {
    if (!agentId) return null;
    const config = agentApiConfig as any;
    return config[agentId] || null;
  }

  /**
   * Get the common/shared configuration block from api_endpoints.json
   * @returns The common config object, or null if not defined
   */
  private getCommonBlock(): any {
    const config = agentApiConfig as any;
    return config['common'] || null;
  }

  /**
   * Get API configuration from agent-specific config, falling back to common block.
   * Resolution order: agent-specific endpoint > common endpoint.
   * Only falls back to common if the agent has a recognized block in the config.
   * @param key - API configuration key (e.g., 'get_agent_response')
   * @param agentId - Optional agent ID to look up agent-specific config
   * @returns The API configuration object, or null if not found in either block
   */
  private getApiConfig(key: string, agentId?: string): any {
    return this.resolveEndpointSource(key, agentId)?.endpointConfig || null;
  }

  /**
   * Determine which block (agent or common) provides the endpoint, used for baseUrl resolution.
   * Only falls back to common if the agent has a recognized block in the config.
   * @param key - API configuration key
   * @param agentId - Optional agent ID
   * @returns Object with the resolved endpoint config and the source block
   */
  private resolveEndpointSource(key: string, agentId?: string): { endpointConfig: any; sourceBlock: any } | null {
    const agentBlock = this.getAgentBlock(agentId);
    if (!agentBlock) return null;

    const commonBlock = this.getCommonBlock();

    if (agentBlock.endpoints?.[key]) {
      return { endpointConfig: agentBlock.endpoints[key], sourceBlock: agentBlock };
    }

    if (commonBlock?.endpoints?.[key]) {
      return { endpointConfig: commonBlock.endpoints[key], sourceBlock: commonBlock };
    }

    return null;
  }

  /**
   * Get URL for an agent API endpoint from config.
   * Resolution: agent-specific endpoint > common endpoint.
   * BaseUrl is taken from the source block that provides the endpoint.
   * @param key - API configuration key
   * @param agentId - Optional agent ID for agent-specific config lookup
   * @returns The API URL (e.g., '/api/agent/response') or empty string if no config
   */
  getApiUrlFromConfig(key: string, agentId?: string): string {
    const resolved = this.resolveEndpointSource(key, agentId);
    if (!resolved) return '';
    const baseUrl = resolved.endpointConfig?.baseUrl ?? resolved.sourceBlock.baseUrl ?? '/api/agent';
    return resolved.endpointConfig?.path ? `${baseUrl}${resolved.endpointConfig.path}` : '';
  }

  /**
   * Check whether an endpoint resolves from the shared `common` block instead of the
   * agent's own block.
   *
   * Callers use this to pick the request contract: centralized (common) agent APIs
   * identify a conversation by `message_id`, while agent-level APIs
   * identify it by `response_id` plus `user_id`/`thread_id`.
   *
   * @param key - API configuration key
   * @param agentId - Optional agent ID
   * @returns true when the endpoint comes from the common block
   */
  usesCommonEndpoint(key: string, agentId?: string): boolean {
    const resolved = this.resolveEndpointSource(key, agentId);
    return !!resolved && resolved.sourceBlock === this.getCommonBlock();
  }

  /**
   * Get HTTP method for an agent API endpoint from config
   * @param key - API configuration key
   * @param agentId - Optional agent ID for agent-specific config lookup
   * @returns The HTTP method (e.g., 'POST', 'GET')
   */
  getApiMethodFromConfig(key: string, agentId?: string): string {
    const config = this.getApiConfig(key, agentId);
    return config?.method || 'GET';
  }

  /**
   * Get complete API configuration for an endpoint
   * @param key - API configuration key
   * @param agentId - Optional agent ID for agent-specific config lookup
   * @returns The complete API configuration object
   */
  getFullApiConfig(key: string, agentId?: string): any {
    return this.getApiConfig(key, agentId);
  }

  /**
   * Get the API base URL for an agent.
   * Resolution: agent block > common block > undefined.
   * Only falls back to common if the agent has a recognized block.
   * @param agentId - Agent ID to look up
   * @returns The base URL for the agent, or undefined if no config
   */
  getBaseUrlForAgent(agentId?: string): string | undefined {
    const agentBlock = this.getAgentBlock(agentId);
    if (!agentBlock) return undefined;
    if (agentBlock.agentApiBaseUrl) return agentBlock.agentApiBaseUrl;

    const commonBlock = this.getCommonBlock();
    return commonBlock?.agentApiBaseUrl || undefined;
  }

  /**
   * Get the API base URL for a specific endpoint, resolving from the block that provides it.
   * Useful when an endpoint comes from common but the agent has its own base URL.
   * @param key - Endpoint key
   * @param agentId - Agent ID
   * @returns The base URL from the agent block (if it exists), otherwise from the source block
   */
  getBaseUrlForEndpoint(key: string, agentId?: string): string | undefined {
    const agentBlock = this.getAgentBlock(agentId);
    if (!agentBlock) return undefined;

    const resolved = this.resolveEndpointSource(key, agentId);
    if (resolved?.sourceBlock?.agentApiBaseUrl) {
      return resolved.sourceBlock.agentApiBaseUrl;
    }
    if (agentBlock.agentApiBaseUrl) return agentBlock.agentApiBaseUrl;
    return undefined;
  }

  /**
   * Get the streaming API base URL for an agent.
   * Resolution: agent block > common block > undefined.
   * Only falls back to common if the agent has a recognized block.
   * @param agentId - Agent ID to look up
   * @returns The streaming base URL for the agent, or undefined if no config
   */
  getStreamingBaseUrlForAgent(agentId?: string): string | undefined {
    const agentBlock = this.getAgentBlock(agentId);
    if (!agentBlock) return undefined;
    if (agentBlock.agentStreamingApiBaseUrl) return agentBlock.agentStreamingApiBaseUrl;

    const commonBlock = this.getCommonBlock();
    return commonBlock?.agentStreamingApiBaseUrl || undefined;
  }

  /**
   * Get the supervisor array for an agent (used for centralized APIs).
   * @param agentId - Agent ID to look up
   * @returns The supervisor array (e.g., ["ria"]) or undefined if not configured
   */
  getSupervisorForAgent(agentId?: string): string[] | undefined {
    const agentBlock = this.getAgentBlock(agentId);
    return agentBlock?.supervisor || undefined;
  }

  /**
   * Returns the endpoint path only (no relative baseUrl prefix).
   * Use with getAuxiliaryApiBaseUrl for GRI or other auxiliary APIs.
   */
  getEndpointPath(key: string, agentId?: string): string {
    const config = this.getApiConfig(key, agentId);
    return config?.path || '';
  }

  /**
   * Returns an auxiliary API base URL defined on the agent block (e.g. griApiBaseUrl).
   */
  getAuxiliaryApiBaseUrl(agentId: string, fieldName: string): string | undefined {
    const agentBlock = this.getAgentBlock(agentId);
    const value = agentBlock?.[fieldName];
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
  }



  /**
   * Get the full API URL with base URL prepended
   * @param endpoint - The API endpoint path
   * @param customBaseUrl - Optional custom base URL to override default
   * @returns Complete URL (e.g., 'https://api.example.com/api/agent/response')
   */
  private getFullApiUrl(endpoint: string, customBaseUrl?: string): string {
    if (customBaseUrl) {
      return `${customBaseUrl}${endpoint}`;
    }
    
    // No default base URL - agents without config should not reach here
    return endpoint;
  }

  /** GRI document APIs accept Content-Type + Authorization only (CORS). */
  private isGriApiBackend(customBaseUrl?: string): boolean {
    if (!customBaseUrl) return false;
    const riaBlock = (agentApiConfig as any)['ria_agent'];
    return riaBlock?.griApiBaseUrl === customBaseUrl;
  }

  /**
   * Get HTTP headers for a request.
   * 
   * - GRI document backend: Content-Type + Authorization only (CORS compatibility)
   * - All other backends: Content-Type + Authorization + custom agent headers
   * 
   * @param token - Optional bearer token (defaults to TokenService.getAccessToken())
   * @param customBaseUrl - The target base URL, used to determine header mode
   * @param additionalHeaders - Optional additional headers
   * @returns HttpHeaders object
   */
  private getHeaders(token?: string, customBaseUrl?: string, additionalHeaders?: { [key: string]: string }): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    // GRI document backend only accepts Content-Type + Authorization (CORS)
    // All other agent backends get the full custom header set
    if (!this.isGriApiBackend(customBaseUrl)) {
      headers = headers.set('user-id', environment.LoginUserID || '');
      headers = headers.set('tenant-id', environment.TenantId || '0');
      headers = headers.set('domain-name', environment.DomainName || '');

      if (environment.LoginUserName) {
        headers = headers.set('x-user-full-name', environment.LoginUserName);
      }
    }

    // Bearer token - use fresh token from TokenService if not provided
    const authToken = token || this.tokenService.getAccessToken();
    if (authToken) {
      headers = headers.set('Authorization', `Bearer ${authToken}`);
    }

    if (additionalHeaders) {
      Object.keys(additionalHeaders).forEach(key => {
        headers = headers.set(key, additionalHeaders[key]);
      });
    }

    return headers;
  }

  /**
   * Generic GET request
   * @param endpoint - The API endpoint path
   * @param token - Optional bearer token
   * @param params - Optional HTTP parameters
   * @param customBaseUrl - Optional custom base URL
   * @returns Observable<T>
   */
  get<T>(
    endpoint: string,
    token?: string,
    params?: HttpParams,
    customBaseUrl?: string
  ): Observable<T> {
    const url = this.getFullApiUrl(endpoint, customBaseUrl);
    const headers = this.getHeaders(token, customBaseUrl);
    return this.http.get<T>(url, { headers, params });
  }

  /**
   * Generic POST request
   * @param endpoint - The API endpoint path
   * @param data - Request body data
   * @param token - Optional bearer token
   * @param customBaseUrl - Optional custom base URL
   * @param responseType - Optional response type ('json' | 'text' | 'blob'), defaults to 'json'
   * @returns Observable<T>
   */
  post<T>(
    endpoint: string,
    data: unknown,
    token?: string,
    customBaseUrl?: string,
    responseType?: 'json' | 'text' | 'blob'
  ): Observable<T> {
    const url = this.getFullApiUrl(endpoint, customBaseUrl);
    const headers = this.getHeaders(token, customBaseUrl);
    if (responseType && responseType !== 'json') {
      return this.http.post(url, data, { headers, responseType: responseType as any }) as unknown as Observable<T>;
    }
    return this.http.post<T>(url, data, { headers });
  }

  /**
   * Generic PUT request
   * @param endpoint - The API endpoint path
   * @param data - Request body data
   * @param token - Optional bearer token
   * @param customBaseUrl - Optional custom base URL
   * @returns Observable<T>
   */
  put<T>(
    endpoint: string,
    data: unknown,
    token?: string,
    customBaseUrl?: string
  ): Observable<T> {
    const url = this.getFullApiUrl(endpoint, customBaseUrl);
    const headers = this.getHeaders(token, customBaseUrl);
    return this.http.put<T>(url, data, { headers });
  }

  /**
   * Generic DELETE request
   * @param endpoint - The API endpoint path
   * @param data - Request body data
   * @param token - Optional bearer token
   * @param customBaseUrl - Optional custom base URL
   * @returns Observable<T>
   */
  delete<T>(
    endpoint: string,
    data?: unknown,
    token?: string,
    customBaseUrl?: string
  ): Observable<T> {
    const url = this.getFullApiUrl(endpoint, customBaseUrl);
    const headers = this.getHeaders(token, customBaseUrl);
    return this.http.delete<T>(url, { headers, body: data });
  }

  /**
   * Generic PATCH request
   * @param endpoint - The API endpoint path
   * @param data - Request body data
   * @param token - Optional bearer token
   * @param customBaseUrl - Optional custom base URL
   * @returns Observable<T>
   */
  patch<T>(
    endpoint: string,
    data: unknown,
    token?: string,
    customBaseUrl?: string
  ): Observable<T> {
    const url = this.getFullApiUrl(endpoint, customBaseUrl);
    const headers = this.getHeaders(token, customBaseUrl);
    return this.http.patch<T>(url, data, { headers });
  }

}

